<template>
  <div
    ref="container"
    class="vch-full-width-slider"
    :class="{ 'vch-full-width-slider--with-disclaimers': withDisclaimers }"
    :data-qa="qaHook"
  >
    <div
      ref="scrollable"
      class="vch-full-width-slider__content"
      :class="{ '-is-dragging': isDragging }"
      @mousedown="dragStart"
    >
      <slot />
    </div>
    <carousel-indicator
      indicator-type="dots"
      item-selector=".vch-full-width-slider__item"
      :pagination-label="paginationLabel"
      :item-label="itemLabel"
      :observe-content="false"
      :current-item="currentItem"
      :use-anchor-scrolling="false"
      :on-indicator-click="scrollToItem"
      :seconds="seconds"
    ></carousel-indicator>
  </div>
</template>

<script>
import { debounce } from '../../utilities/timing';

const scrollDebounceTimeout = 100;

export default {
  name: 'full-width-slider',
  props: {
    paginationLabel: {
      type: String,
      required: false,
    },
    itemLabel: {
      type: String,
      required: false,
    },
    qaHook: {
      type: String,
      required: false,
    },
    withDisclaimers: {
      type: Boolean,
      required: false,
      default: false,
    },
    seconds: {
      type: String,
      required: false,
    },
  },
  data() {
    return {
      isDragging: false,
      scrollableEl: null,
      microMoveMargin: 15,
      items: [],
      intersectingItemIndex: 0,
      currentItem: 0,
      cardWidth: 0,
      observer: null,
      onScrollableScroll: null,
    };
  },
  mounted() {
    this.scrollableEl = this.$refs.scrollable.querySelector('.vch-full-width-slider__list');
    if (!this.scrollableEl) return;

    this.items = Array.from(this.scrollableEl.querySelectorAll('.vch-full-width-slider__item'));

    this.observer = new IntersectionObserver(this.updateIntersectingItemIndex, {
      threshold: 0.3,
    });

    this.items.forEach((item) => {
      this.observer.observe(item);
    });

    window.addEventListener('resize', this.updateCardWidths);

    this.$nextTick(function () {
      this.updateCardWidths();
      this.onScrollableScroll = debounce(this.onSliderScrolled, scrollDebounceTimeout);
      this.scrollableEl.addEventListener('scroll', this.onScrollableScroll);
      this.setCurrentItemAttributes(0);
    });
  },
  beforeDestroy() {
    if (this.observer) {
      this.items.forEach((item) => {
        this.observer.unobserve(item);
      });
      this.observer.disconnect();
    }
    if (this.scrollableEl) {
      this.scrollableEl.removeEventListener('scroll', this.onScrollableScroll);
    }
    window.removeEventListener('resize', this.updateCardWidths);
  },
  methods: {
    updateCardWidths() {
      const containerWidth = this.$refs.container.offsetWidth,
        widthInColumns = 11,
        gutterWidth = 16;
      const gridColWidth = (containerWidth - (widthInColumns - 1) * gutterWidth) / 12;
      this.cardWidth =
        window.innerWidth < 1024
          ? containerWidth
          : gridColWidth * widthInColumns + (widthInColumns - 1) * gutterWidth;

      // Set the calculated width of each card (11/12 grid columns)
      this.items.forEach((item) => {
        item.style.minWidth = `${this.cardWidth}px`;
      });

      // Set the width of the psuedo element spacer at the end of the carousel
      const contentMargin = window.innerWidth - containerWidth - gutterWidth;
      this.scrollableEl.style.setProperty('--end-spacing', `${contentMargin}px`);
    },
    dragStart(startEvent) {
      const startOffset = startEvent.clientX;
      const scrollStart = this.scrollableEl.scrollLeft;

      const dragMove = (moveEvent) => {
        moveEvent.preventDefault();
        const dx = moveEvent.clientX - startOffset;
        this.isDragging = this.isDragging || Math.abs(dx) > this.microMoveMargin;
        this.scrollableEl.scrollLeft = scrollStart - dx;
      };

      const dragStop = () => {
        this.scrollToItem(parseInt(this.intersectingItemIndex));

        // Give the smooth scroll a half second to finish before turning the snap back on.
        setTimeout(() => {
          this.isDragging = false;
        }, 500);

        this.$refs.scrollable.removeEventListener('mousemove', dragMove);
        this.$refs.scrollable.removeEventListener('mouseup', dragStop);
        this.$refs.scrollable.removeEventListener('mouseleave', dragStop);
        this.$refs.scrollable.removeEventListener('blur', dragStop);
      };

      this.$refs.scrollable.addEventListener('mousemove', dragMove);
      this.$refs.scrollable.addEventListener('mouseup', dragStop);
      this.$refs.scrollable.addEventListener('mouseleave', dragStop);
      this.$refs.scrollable.addEventListener('blur', dragStop);
    },
    updateIntersectingItemIndex([e]) {
      if (e.isIntersecting) {
        this.intersectingItemIndex = this.items.indexOf(e.target).toString();
      }
    },
    scrollToItem(index) {
      if (!this.scrollableEl || index < 0 || index > this.items.length - 1) return;
      this.scrollableEl.scrollTo({
        top: 0,
        left: this.items[index].offsetLeft,
        behavior: 'smooth',
      });
    },
    onSliderScrolled(e) {
      // Sets the current item based on the scroll offset
      this.currentItem = Math.round(e.target.scrollLeft / this.cardWidth);
      this.setCurrentItemAttributes(this.currentItem);
    },
    setCurrentItemAttributes(itemIndex) {
      this.items.forEach((item, idx) => {
        if (idx === itemIndex) {
          item.setAttribute('aria-hidden', 'false');
          item.querySelectorAll('a').forEach((anchor) => anchor.removeAttribute('tabindex'));
        } else {
          item.setAttribute('aria-hidden', 'true');
          item.querySelectorAll('a').forEach((anchor) => anchor.setAttribute('tabindex', '-1'));
        }
      });
    },
  },
};
</script>
